"use client";

import { isNotUndefined } from "@faire/typescript";
import fetchUserAddresses from "@faire/web-api--source/endpoints/www/api/retailer/address/list/post";
import fetchUser from "@faire/web-api--source/endpoints/www/api/user/get";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { getUser } from "@faire/web/common/serialized-data/getUser";
import React, { createContext, useContext, useEffect, useState } from "react";
import { create, StoreApi, useStore } from "zustand";

import { getLocaleCountryUrlPrefix } from "../../serialized-data/getLocaleCountryUrlPrefix";
import { isLoggedInRetailer } from "../../serialized-data/isLoggedInRetailer";

import { IUserStore } from "./IUserStore";
import {
  browserLevelLocaleOverride,
  getUserStoreFieldsFromUser,
  logOut,
} from "./utils";

type Store = IUserStore & {
  addressDeletableMap: Record<string, boolean>;
};

const createUserStore = (user: IUser | undefined) =>
  create<Store>((set, get) => {
    // Update every field that depends on the user
    const setUser = (user: IUser | undefined) => {
      const fields = getUserStoreFieldsFromUser(user);
      set({ ...fields });
    };

    return {
      ...getUserStoreFieldsFromUser(user),

      // User
      setUser,
      patchUser: (_user: Partial<IUser>) => {
        const user = IUser.build({ ...get().user, ..._user });
        setUser(user);
      },

      // Addresses
      addresses: [],
      userAddresses: [],
      fetchUserAddresses: async () => {
        if (!get().user || !isLoggedInRetailer()) {
          return;
        }
        const response = await fetchUserAddresses({ address_filters: [] });
        const addressDeletableMap = (response?.addresses ?? []).reduce(
          (acc, address) => {
            if (!address.address?.token) {
              return acc;
            }
            return {
              ...acc,
              [address.address.token]: !!address.is_deletable,
            };
          },
          {} as Record<string, boolean>
        );
        set({ addressDeletableMap });
        const addresses = (response?.addresses ?? [])
          .map((address) => address.address)
          .filter(isNotUndefined);
        set({ addresses, userAddresses: addresses });
      },
      addressDeletableMap: {},
      addressIsDeletable: (addressToken: string) => {
        if (!addressToken) {
          return false;
        }
        return !!get().addressDeletableMap[addressToken];
      },

      // Both user and addresses
      fetchUser: async () => {
        await Promise.all([
          (async () => {
            const user = await fetchUser();
            setUser(user);
          })(),
          get().fetchUserAddresses(),
        ]);
      },

      // Other fields
      logOut,
      browserLevelLocaleOverride: browserLevelLocaleOverride(),
      localeCountryUrlPrefix: getLocaleCountryUrlPrefix(),
    };
  });

const UserStoreContext = createContext<StoreApi<IUserStore> | undefined>(
  undefined
);

export const UserStoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userStore] = useState<StoreApi<IUserStore> | undefined>(() =>
    createUserStore(getUser())
  );

  useEffect(() => {
    void userStore?.getState().fetchUserAddresses();
  }, [userStore]);

  return (
    <UserStoreContext.Provider value={userStore}>
      {children}
    </UserStoreContext.Provider>
  );
};

export const useUserStore = () => {
  const userStore = useContext(UserStoreContext);
  if (!userStore) {
    throw new Error("useUserStore must be used within a UserStoreProvider");
  }
  return useStore(userStore);
};

/**
 * This is an empty component that is used to include this useUserStore
 * module in other layout chunks that don't use it.
 */
export const VoidUserStore: React.FC = () => {
  useUserStore();
  return null;
};
